import EventBroaderContext from "../EventBroaderContext/EventBroaderContext";
import EventSummary from "../EventSummary/EventSummary";
import AlertError from "../../AlertError/AlertError";
import { Analysis, Event } from "@unruly-osdk/sdk";
import { Osdk } from "@osdk/client";
import styles from "./EventContent.module.css";
import CustomButton from "../CustomButton/CustomButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import SearchIcon from "@mui/icons-material/Search";
import ShareIcon from "@mui/icons-material/Share";
import BoltIcon from "@mui/icons-material/Bolt";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import EventContentSkeleton from "./EventContentSkeleton";
import { useState, useEffect } from "react";
import { EntityType } from "../../common-types/Entity";
import { usePageTitle } from "../../context/usePageTitle";
import { useEventDeepDive } from "../../hooks/useEventDeepDive";

interface EventContentProps {
  event?: Osdk.Instance<Event>;
  onLinkClick?: React.MouseEventHandler<HTMLAnchorElement>;
  openLinksInNewTab?: boolean;
  onEntityClick?: (id: string, type: EntityType) => void;
  context?: string;
  isContextLoading?: boolean;
  isEventLoading?: boolean;
  fetchContextFromAPI?: boolean;
  isEventError?: boolean;
  isContextError?: boolean;
  onBackClick: () => void;
  eventId?: string;
  userId?: string;
  onOpenAnalysis?: (analysis: Osdk.Instance<Analysis>, eventId: string) => void;
  showShareDialog?: (eventId: string) => void;
}

const EventContent: React.FC<EventContentProps> = ({
  event,
  onLinkClick,
  openLinksInNewTab,
  onEntityClick,
  context,
  isContextLoading,
  isEventLoading,
  fetchContextFromAPI,
  isEventError,
  isContextError,
  onBackClick,
  userId,
  onOpenAnalysis,
  showShareDialog,
}: EventContentProps) => {
  const [copySuccess, setCopySuccess] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const homeUrl = import.meta.env.VITE_FOUNDRY_APP_HOME_URL;
  const { setPageTitle } = usePageTitle();

  const {
    status: deepDiveStatus,
    isLoading: isDeepDiveLoading,
    handleRequestDeepDive,
    handleOpenDeepDive,
  } = useEventDeepDive({
    eventId: event?.id || "",
    eventName: event?.name ?? undefined,
    sourceUrl: event?.newsSourceUrl ?? undefined,
    userId,
    onOpenAnalysis,
  });

  const deepDiveLabel =
    deepDiveStatus === "completed"
      ? "Open Deep-Dive"
      : deepDiveStatus === "requested"
        ? "Requested"
        : deepDiveStatus === "processing"
          ? "Processing"
          : deepDiveStatus === "failed"
            ? "Retry Deep-Dive"
            : "Deep-Dive";

  const deepDiveTitle =
    deepDiveStatus === "completed"
      ? "Open the completed deep dive analysis"
      : deepDiveStatus === "requested"
        ? "Deep dive has been requested"
        : deepDiveStatus === "processing"
          ? "Deep dive is being processed"
          : deepDiveStatus === "failed"
            ? "Retry the deep dive analysis"
            : "Run a deep dive analysis on this event";

  // Set page title when event loads
  useEffect(() => {
    if (event) {
      const eventDate = event.startDate
        ? new Date(event.startDate).toLocaleDateString()
        : "";
      setPageTitle(event.name || "Event Details", eventDate);
    }

    // Cleanup on unmount
    return () => {
      setPageTitle("", "");
    };
  }, [event, setPageTitle]);

  const handleCopyPermalink = async () => {
    if (!event?.id) {
      console.error("No event ID available");
      return;
    }
    try {
      const permalink = `${homeUrl}/event/${event.id}`;
      await navigator.clipboard.writeText(permalink);

      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    } catch (err) {
      console.error("Failed to copy permalink:", err);
    }
  };

  return (
    <section className={styles["container"]}>
      <div className={styles["headerContainer"]}>
        <CustomButton variant="text" icon={ArrowBackIcon} onClick={onBackClick}>
          Back
        </CustomButton>

        {/* Desktop: inline buttons */}
        <div className={styles.headerActions}>
          {event && (
            <>
              <CustomButton
                variant="text"
                icon={SearchIcon}
                onClick={
                  deepDiveStatus === "completed"
                    ? handleOpenDeepDive
                    : handleRequestDeepDive
                }
                disabled={
                  isDeepDiveLoading ||
                  (deepDiveStatus !== "idle" &&
                    deepDiveStatus !== "completed" &&
                    deepDiveStatus !== "failed")
                }
                loading={isDeepDiveLoading}
                title={deepDiveTitle}
              >
                {deepDiveLabel}
              </CustomButton>
              {showShareDialog && (
                <CustomButton
                  variant="text"
                  icon={ShareIcon}
                  onClick={() => showShareDialog(event.id)}
                  title="Share this event to LinkedIn"
                >
                  Share
                </CustomButton>
              )}
            </>
          )}
          <CustomButton
            variant="text"
            icon={ContentCopyIcon}
            onClick={handleCopyPermalink}
            title="Copy event permalink"
          >
            {copySuccess ? "Copied!" : "Copy Permalink"}
          </CustomButton>
        </div>

        {/* Mobile: dropdown menu */}
        <div className={styles.mobileMenu}>
          <CustomButton
            variant="text"
            icon={BoltIcon}
            rightIcon={ArrowDropDownIcon}
            onClick={(e) => setMenuAnchor(e.currentTarget)}
          >
            Actions
          </CustomButton>
          <Menu
            anchorEl={menuAnchor}
            open={Boolean(menuAnchor)}
            onClose={() => setMenuAnchor(null)}
          >
            {event && [
              <MenuItem
                key="deep-dive"
                onClick={() => {
                  setMenuAnchor(null);
                  if (deepDiveStatus === "completed") {
                    handleOpenDeepDive();
                  } else {
                    handleRequestDeepDive();
                  }
                }}
                disabled={
                  isDeepDiveLoading ||
                  (deepDiveStatus !== "idle" &&
                    deepDiveStatus !== "completed" &&
                    deepDiveStatus !== "failed")
                }
              >
                <ListItemIcon>
                  <SearchIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{deepDiveLabel}</ListItemText>
              </MenuItem>,
              showShareDialog && (
                <MenuItem
                  key="share"
                  onClick={() => {
                    setMenuAnchor(null);
                    showShareDialog(event.id);
                  }}
                >
                  <ListItemIcon>
                    <ShareIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText>Share</ListItemText>
                </MenuItem>
              ),
            ]}
            <MenuItem
              key="copy"
              onClick={() => {
                setMenuAnchor(null);
                handleCopyPermalink();
              }}
            >
              <ListItemIcon>
                <ContentCopyIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>
                {copySuccess ? "Copied!" : "Copy Permalink"}
              </ListItemText>
            </MenuItem>
          </Menu>
        </div>
      </div>
      {isEventLoading && <EventContentSkeleton />}
      {isEventError && (
        <AlertError errorMessage="Something went wrong while loading the event details, please try again. If the problem persists, contact Unruly support at accounts@unrulycorp.com." />
      )}
      {event && (
        <>
          <EventSummary event={event} />

          <EventBroaderContext
            event={event}
            onLinkClick={onLinkClick}
            openLinksInNewTab={openLinksInNewTab}
            context={context}
            isContextLoading={isContextLoading}
            fetchContextFromAPI={fetchContextFromAPI}
            isContextError={isContextError}
            onEntityClick={onEntityClick}
          />
        </>
      )}
    </section>
  );
};

export default EventContent;
